import { useMemo, useRef, useState } from "react";

import {
    Box,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";

import { useAppSelector } from "app/hooks";

import { PlayerCard, PlayerCardRef } from "features/videoPlayer/PlayerCard";
import { selectInWindowMode } from "features/core/store/selectors";
import { HlsPlayer } from "features/videoPlayer/HlsPlayer";
import { PlayerCardStatus } from "features/videoPlayer/PlayerCardStatus";
import { PlayerRef } from "features/videoPlayer/types/PlayerRef";
import { useStartDiagnosticsTestStreamMutation } from "services/streamingApi/streamingPodManager.api";
import { PlayArrow } from "@mui/icons-material";
import { HlsStats } from "features/videoPlayer/types/HlsStats";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { StreamPlayerModeType } from "types/StreamPlayerModeType";

export interface DiagnosticsPlayerProps {
    sessionId: string;
    operationId?: string;
}

export function DiagnosticsPlayer(props: DiagnosticsPlayerProps) {
    const [
        startDiagnosticsTestStream,
        { data: streamUrl, isLoading, isError: streamFailedToStart },
    ] = useStartDiagnosticsTestStreamMutation();

    const appInsights = useAppInsightsContext();

    const playerCardRef = useRef<PlayerCardRef>(null);
    const playerRef = useRef<PlayerRef>(null);
    const inWindowMode = useAppSelector(selectInWindowMode);

    const [playerMode, setPlayerMode] = useState<StreamPlayerModeType>(
        StreamPlayerModeType.LiveStream,
    );

    const [bufferStalled, setBufferStalled] = useState<number>(0);
    const [hlsErrors, setHlsErrors] = useState<Map<string, number>>(new Map());

    const [stats, setStats] = useState<HlsStats>({
        bandwidthEstimate: 0,
        bufferLength: 0,
        latency: 0,
        droppedFrames: 0,
        width: 0,
        height: 0,
    });

    const resetValues = () => {
        setStats({
            bandwidthEstimate: 0,
            bufferLength: 0,
            latency: 0,
            droppedFrames: 0,
            width: 0,
            height: 0,
        });

        setBufferStalled(0);
        setHlsErrors(new Map());
    };

    const handlePlayerModeChange = (event: SelectChangeEvent) => {
        setPlayerMode(event.target.value as StreamPlayerModeType);
    };

    const cachedHlsPlayerCard = useMemo(() => {
        return (
            <PlayerCard
                sx={{ width: "70%", justifySelf: "center" }}
                ref={playerCardRef}
                cardActions={
                    <>
                        <Box
                            sx={{
                                flexGrow: 1,
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                <IconButton
                                    onClick={() => {
                                        resetValues();
                                        startDiagnosticsTestStream({
                                            sessionId: props.sessionId,
                                        });
                                    }}
                                    aria-label="Play"
                                    title="(Re)start diagnostic stream"
                                >
                                    <PlayArrow />
                                </IconButton>
                            </Typography>
                        </Box>
                    </>
                }
            >
                {!streamFailedToStart && isLoading && (
                    <PlayerCardStatus mode={"progress"} />
                )}

                {streamFailedToStart && (
                    <PlayerCardStatus
                        mode={"status"}
                        statusIcon={<VideocamOffIcon sx={{ fontSize: 150 }} />}
                    />
                )}

                {!isLoading && !streamFailedToStart && (
                    <HlsPlayer
                        ref={playerRef}
                        url={streamUrl}
                        playMode={"play"}
                        playSpeed={1}
                        enableAudio={false}
                        audioEnabledByDefault={false}
                        onClickVideo={() => {
                            if (!document.fullscreenEnabled) return;

                            if (playerCardRef.current?.root) {
                                if (!document.fullscreenElement) {
                                    playerCardRef.current.root.requestFullscreen(
                                        {
                                            navigationUI: "show",
                                        },
                                    );
                                } else {
                                    document.exitFullscreen();
                                }
                            }
                        }}
                        diagnosticCorrelationId={props.sessionId}
                        onStreamQualityErrorCountChange={() => {
                            setBufferStalled((prev) => prev + 1);
                        }}
                        onStreamErrorOccured={(error) => {
                            setHlsErrors(
                                hlsErrors.set(
                                    error,
                                    (hlsErrors.get(error) ?? 0) + 1,
                                ),
                            );
                        }}
                        playerMode={playerMode}
                        onStatsChanged={setStats}
                    />
                )}
            </PlayerCard>
        );
    }, [inWindowMode, isLoading, streamFailedToStart, streamUrl, playerMode]);

    return (
        <Box>
            {cachedHlsPlayerCard}

            <Box sx={{ marginTop: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "30%" }}>
                                HLS player settings
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Player Mode</TableCell>
                            <TableCell>
                                <Select
                                    id="playermode-select"
                                    label="Player Mode"
                                    defaultValue={
                                        StreamPlayerModeType.LiveStream
                                    }
                                    onChange={handlePlayerModeChange}
                                >
                                    <MenuItem
                                        value={StreamPlayerModeType.LiveStream}
                                    >
                                        LiveStream
                                    </MenuItem>
                                    <MenuItem
                                        value={StreamPlayerModeType.Playback}
                                    >
                                        PlayBack
                                    </MenuItem>
                                    <MenuItem
                                        value={StreamPlayerModeType.Diagnostic}
                                    >
                                        Diagnostic
                                    </MenuItem>
                                </Select>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>

            <Box sx={{ marginTop: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "30%" }}>
                                HLS player stats
                            </TableCell>
                            <TableCell>Values</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>Current session id</TableCell>
                            <TableCell>{props.sessionId}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>App Insights operation id</TableCell>
                            <TableCell>
                                {appInsights.core
                                    .getTraceCtx(false)
                                    ?.getTraceId()}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Bandwidth</TableCell>
                            <TableCell>
                                {stats.bandwidthEstimate
                                    ? (stats.bandwidthEstimate / 8000).toFixed(
                                          2,
                                      )
                                    : 0}{" "}
                                kb/s
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Latency</TableCell>
                            <TableCell>
                                {stats.latency.toFixed(2)} seconds
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Resolution</TableCell>
                            <TableCell>{`${stats.width}x${stats.height}`}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Buffer Length</TableCell>
                            <TableCell>
                                {stats.bufferLength.toFixed(2)} seconds
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Buffer Stalled</TableCell>
                            <TableCell>{bufferStalled} times</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>Dropped Frames</TableCell>
                            <TableCell>{stats.droppedFrames}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </Box>
            <Box sx={{ marginTop: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: "30%" }}>
                                HLS player errors
                            </TableCell>
                            <TableCell>Occurance</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {hlsErrors &&
                            [...hlsErrors.entries()].map(([key, value]) => (
                                <TableRow key={key}>
                                    <TableCell>{key}</TableCell>
                                    <TableCell>{value}</TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
            </Box>
        </Box>
    );
}
